import { useState } from "react";
import { Download, Eye, FileText, Loader2, Paperclip } from "lucide-react";

import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import { Badge } from "@/components/ui/badge";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import {
  GraphAttachmentPdfDialog,
  type GraphAttachmentRef,
} from "@/components/common/GraphAttachmentPdfDialog";

import type { GraphMailAttachmentSummary } from "./kaiToolDownloads";

/** True for files the in-app dialog can render inline (PDF / image). */
function isPreviewable(filename: string): boolean {
  return /\.(pdf|png|jpe?g|gif|webp|avif|bmp)$/i.test(filename);
}

/** Download button for a single Graph-mail document attachment. */
function GraphMailDownloadButton({
  att,
}: {
  att: GraphMailAttachmentSummary;
}) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = async () => {
    setDownloading(true);
    try {
      await api.downloadGraphAttachment(
        att.mailboxId,
        att.messageId,
        att.attachmentId,
        att.filename,
      );
    } catch (err) {
      console.warn("Failed to download mail attachment:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={downloading}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-foreground/[0.03] px-2.5 py-1 text-[11px] text-foreground/90 transition-colors hover:bg-accent/40",
        downloading && "opacity-60",
      )}
    >
      {downloading ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Download className="size-3.5" />
      )}
      Download
    </button>
  );
}

/**
 * Compact card rendering the downloadable document attachments surfaced by a
 * Graph-mail tool's `graph_attachments` block: one row per file with its
 * subject/category subtitle, a Download button, and (for PDFs / images) a
 * Preview button that opens {@link GraphAttachmentPdfDialog} in-app. Excel files
 * are download-only. Non-downloadable refs (archived / purged blobs) show a
 * muted "Not available" pill.
 */
export function KaiGraphMailAttachmentsCard({
  attachments,
  embedded = false,
}: {
  attachments: GraphMailAttachmentSummary[];
  /** When nested inside another Kai artifact, skip the extra glass panel. */
  embedded?: boolean;
}) {
  const { formatDate } = useDateFormatter();
  const [preview, setPreview] = useState<GraphAttachmentRef | null>(null);

  if (attachments.length === 0) return null;

  return (
    <div
      className={cn(
        "flex flex-col gap-2",
        embedded
          ? "border-t border-border/40 pt-3"
          : "glass-panel my-1 rounded-xl p-3",
      )}
    >
      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <Paperclip className="size-4 shrink-0" />
        {attachments.length === 1
          ? "Mail attachment"
          : `${attachments.length} mail attachments`}
      </span>

      <div className="flex flex-col divide-y divide-border/40">
        {attachments.map((att) => {
          const previewable = att.downloadable && isPreviewable(att.filename);
          return (
            <div
              key={att.key}
              className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground">
                  <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{att.filename}</span>
                  {att.category && (
                    <Badge
                      variant="secondary"
                      className="shrink-0 text-[10px] font-medium"
                    >
                      {att.category.replace(/_/g, " ")}
                    </Badge>
                  )}
                </span>
                <span className="truncate text-[11px] text-muted-foreground">
                  {[
                    att.messageSubject,
                    att.receivedAt ? formatDate(att.receivedAt) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>

              <div className="flex shrink-0 items-center gap-1.5">
                {!att.downloadable ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-border/50 px-2.5 py-1 text-[11px] text-muted-foreground"
                    title="The source file is no longer stored for this attachment."
                  >
                    Not available
                  </span>
                ) : (
                  <>
                    {previewable && (
                      <button
                        type="button"
                        onClick={() =>
                          setPreview({
                            mailboxId: att.mailboxId,
                            messageId: att.messageId,
                            attachmentId: att.attachmentId,
                            filename: att.filename,
                          })
                        }
                        className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-foreground/[0.03] px-2.5 py-1 text-[11px] text-foreground/90 transition-colors hover:bg-accent/40"
                      >
                        <Eye className="size-3.5" />
                        Preview
                      </button>
                    )}
                    <GraphMailDownloadButton att={att} />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <GraphAttachmentPdfDialog
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
        attachment={preview}
        previewDescription="Mail attachment preview"
      />
    </div>
  );
}
